/**
 * Analytics Inspector store.
 *
 * Jotai-backed ring buffer of dispatched analytics events plus filter/pause
 * state. Persists to `sessionStorage` so refreshes don't wipe history, but
 * intentionally does NOT use localStorage — event history shouldn't haunt
 * other tabs or survive closing the window.
 *
 * Mutations go through the imperative helpers below so non-React call sites
 * (notably `logTrackedAnalyticsEvent` in `../debugger.ts`) can push without
 * needing a hook.
 */

import { atom, getDefaultStore } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import type { AnalyticsInspectorEvent, AnalyticsInspectorFiltersState } from './types';

const EVENTS_STORAGE_KEY = 'analytics-inspector:events';
const PAUSED_STORAGE_KEY = 'analytics-inspector:paused';
const FILTERS_STORAGE_KEY = 'analytics-inspector:filters';

/**
 * Ring buffer cap. Keeps the panel snappy on long sessions and bounds
 * sessionStorage growth (each event is typically < 1 KB serialized).
 */
export const ANALYTICS_INSPECTOR_MAX_EVENTS = 200;

/**
 * SSR-safe sessionStorage adapter compatible with `atomWithStorage`. Falls
 * back to the initial value on the server and during `sessionStorage` failures
 * (quota / disabled / cross-origin sandbox) so the panel never throws.
 */
const createSessionStorageAdapter = <T>(initialValue: T) => ({
  getItem: (key: string): T => {
    if (typeof window === 'undefined') return initialValue;
    try {
      const raw = window.sessionStorage.getItem(key);
      if (raw === null) return initialValue;
      return JSON.parse(raw) as T;
    } catch {
      return initialValue;
    }
  },
  setItem: (key: string, value: T) => {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      // sessionStorage may throw under quota / private browsing — silently
      // drop. Losing history is preferable to crashing dev tooling.
    }
  },
  removeItem: (key: string) => {
    if (typeof window === 'undefined') return;
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      // see setItem note above.
    }
  },
});

const EVENTS_INITIAL: AnalyticsInspectorEvent[] = [];
const FILTERS_INITIAL: AnalyticsInspectorFiltersState = {
  enabledDestinations: null,
  search: '',
};

export const analyticsInspectorEventsAtom = atomWithStorage<AnalyticsInspectorEvent[]>(
  EVENTS_STORAGE_KEY,
  EVENTS_INITIAL,
  createSessionStorageAdapter<AnalyticsInspectorEvent[]>(EVENTS_INITIAL),
  { getOnInit: true }
);

export const analyticsInspectorPausedAtom = atomWithStorage<boolean>(
  PAUSED_STORAGE_KEY,
  false,
  createSessionStorageAdapter<boolean>(false),
  { getOnInit: true }
);

export const analyticsInspectorFiltersAtom =
  atomWithStorage<AnalyticsInspectorFiltersState>(
    FILTERS_STORAGE_KEY,
    FILTERS_INITIAL,
    createSessionStorageAdapter<AnalyticsInspectorFiltersState>(FILTERS_INITIAL),
    { getOnInit: true }
  );

/**
 * Derived: every destination name ever observed in the current event log,
 * sorted alphabetically. Drives the filter chip row in the UI.
 */
export const analyticsInspectorDestinationsAtom = atom<string[]>((get) => {
  const events = get(analyticsInspectorEventsAtom);
  const seen = new Set<string>();
  for (const event of events) {
    for (const destination of event.destinations) {
      seen.add(destination.destination);
    }
  }
  return [...seen].sort();
});

/**
 * Monotonic counter combined with the wall-clock timestamp to guarantee
 * unique IDs even when multiple events fire in the same millisecond.
 */
let sequence = 0;
function nextId(): string {
  sequence += 1;
  return `${Date.now().toString(36)}-${sequence.toString(36)}`;
}

/**
 * Append one event to the ring buffer. Skipped silently when paused.
 *
 * This is the imperative entry point used by `logTrackedAnalyticsEvent` —
 * called outside of React, so it goes through `getDefaultStore()` rather
 * than a hook.
 */
export function pushAnalyticsInspectorEvent(
  payload: Omit<AnalyticsInspectorEvent, 'id' | 'timestamp'>
): void {
  const store = getDefaultStore();
  if (store.get(analyticsInspectorPausedAtom)) return;

  const event: AnalyticsInspectorEvent = {
    id: nextId(),
    timestamp: Date.now(),
    ...payload,
  };

  const current = store.get(analyticsInspectorEventsAtom);
  // Newest-first; truncate to MAX so sessionStorage stays bounded even on
  // long-running sessions.
  const next = [event, ...current].slice(0, ANALYTICS_INSPECTOR_MAX_EVENTS);
  store.set(analyticsInspectorEventsAtom, next);
}

export function clearAnalyticsInspectorEvents(): void {
  getDefaultStore().set(analyticsInspectorEventsAtom, []);
}
